import { useEffect, useState } from "react";
import { Seo, serviceSchema, breadcrumbSchema } from "@/lib/seo";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { money } from "@/lib/api";
import { SectionHead } from "@/pages/Home";
import { Check, ArrowRight, Loader2, Star, Zap, Shield, Plus, X, Sparkles, Send, FileText, Bot, Landmark, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const STARTER_PAGES = [
  { t: "Home", d: "Hero, services overview, about preview, testimonials, strong CTA, contact form." },
  { t: "Services", d: "Full services list, individual descriptions, Why Choose Us, CTA." },
  { t: "About", d: "Company story, owner/team intro, mission statement, experience & qualifications." },
  { t: "Contact", d: "Contact form, phone, email, Google Map, business hours." },
];

const STARTER_FEATURES = [
  "Mobile responsive design", "Fast loading performance", "SSL security", "Contact form",
  "Google Maps integration", "Social media links", "Click-to-call phone number", "Click-to-email",
  "Photo gallery (up to 10 images)", "Basic SEO Foundation (titles, meta, image optimization)",
  "Google Analytics ready", "Google Search Console ready", "One round of revisions",
];

const INDUSTRY_VARIATIONS = [
  { t: "Restaurant", pages: "Home · Menu · About · Contact", extras: ["Downloadable PDF menu", "Reservation button (OpenTable)", "Google Reviews section"] },
  { t: "Roofing", pages: "Home · Roofing Services · About · Contact", extras: ["Financing button", "Insurance claims CTA", "Free estimate form", "Storm damage info section"] },
  { t: "Lawn Care", pages: "Home · Services · About · Contact", extras: ["Service area map", "Before & after photos", "Seasonal specials", "Free estimate form"] },
];

function priceDisplay(p) {
  if (p.price_label) return p.price_label;
  const prefix = p.price_prefix ? `${p.price_prefix} ` : "";
  return `${prefix}${money(p.price)}`;
}

export default function Services() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/services-pricing").then((r) => setData(r.data)).catch(() => {}); }, []);

  const packages = data?.packages || [];
  const upgrades = data?.upgrades || [];
  const care = data?.care_plan;

  const [selected, setSelected] = useState([]);
  const [showReq, setShowReq] = useState(false);
  const toggle = (name) => setSelected((s) => (s.includes(name) ? s.filter((n) => n !== name) : [...s, name]));
  const parsePrice = (p) => { const m = String(p || "").match(/\d[\d,]*/); return m ? parseInt(m[0].replace(/,/g, ""), 10) : 0; };
  const isVariable = (p) => /from|custom|quote/i.test(String(p || ""));
  const estTotal = selected.reduce((sum, n) => sum + parsePrice((upgrades.find((u) => u.name === n) || {}).price), 0);
  const hasVariable = selected.some((n) => isVariable((upgrades.find((u) => u.name === n) || {}).price));

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <Seo
        title="Website Design & Business Services | ATF WebWorks"
        description="Custom website builds, hosting & care plans, and AI-powered business growth services from ATF WebWorks. Starting at $997."
        path="/services"
        jsonLd={[serviceSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Services", path: "/services" }])]}
      />
      <SectionHead label="Services & Packages" title="Simple. Professional. Affordable. Built to grow." />
      <p className="text-gray-400 mt-5 max-w-2xl">
        We do more than build websites. Plan it, build it, launch it, and grow it — get a professional
        site. Transparent pricing, clear deliverables, a proven process.
      </p>

      {/* PACKAGE LADDER */}
      {packages.length === 0 ? (
        <div className="flex justify-center py-32"><Loader2 className="animate-spin text-gold" size={32} /></div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-6 mt-16 items-stretch">
          {packages.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              data-testid={`package-${p.id}`}
              className={`flex flex-col p-8 border ${p.highlight ? "border-gold bg-navy-700" : "border-white/10 bg-navy-800"}`}>
              {p.highlight && <span className="text-[10px] uppercase tracking-[0.2em] bg-gold text-navy px-3 py-1 font-bold self-start mb-4">{p.highlight_label || "Most Popular"}</span>}
              <h3 className="font-heading text-2xl text-white">{p.name}</h3>
              <p className="text-gray-400 text-sm mt-2 mb-6 min-h-[56px]">{p.best_for || p.tagline}</p>
              <div className="mb-2"><span className="font-heading text-4xl text-white" data-testid={`package-price-${p.id}`}>{priceDisplay(p)}</span></div>
              <div className="text-xs text-gray-500 mb-6">{p.timeline_label ? `${p.timeline_label}: ${p.timeline}` : p.timeline}</div>
              <ul className="space-y-3 mb-8 flex-1">
                {p.deliverables.map((d) => (
                  <li key={d} className="flex items-start gap-3 text-sm text-gray-300"><Check size={16} className="text-gold shrink-0 mt-0.5" /> {d}</li>
                ))}
              </ul>
              <button onClick={() => navigate(`/start?package=${encodeURIComponent(p.name)}`)} data-testid={`select-package-${p.id}`}
                className={`w-full py-3.5 font-semibold flex items-center justify-center gap-2 transition-colors ${p.highlight ? "bg-gold text-navy hover:bg-gold-light" : "border border-white/25 text-white hover:bg-white/5"}`}>
                {p.cta_label || "Get Started"} <ArrowRight size={16} />
              </button>
            </motion.div>
          ))}
        </div>
      )}

      {/* FUND & GROW — beyond websites */}
      <section className="mt-28" data-testid="fund-grow">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-[11px] uppercase tracking-[0.2em] text-gold font-semibold">Fund &amp; Grow</span>
        </div>
        <h2 className="font-heading text-3xl md:text-4xl text-white">More than a website</h2>
        <p className="text-gray-400 mt-4 max-w-2xl">
          Whether you're just starting out or ready to scale, we help you fund the business and grow it — not just look good online.
        </p>
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          {/* Business Plan */}
          <div className="border border-white/10 bg-navy-800 p-8 flex flex-col" data-testid="service-business-plan">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold"><Landmark size={20} /></div>
              <span className="text-[11px] uppercase tracking-[0.2em] text-gold">Fund it</span>
            </div>
            <h3 className="font-heading text-2xl text-white">Professional Business Plan</h3>
            <p className="text-gray-400 text-sm mt-2 mb-5">AI-built, investor-ready plan with grant &amp; funding help. Build it free — pay only to unlock your polished PDF.</p>
            <div className="mb-5"><span className="font-heading text-4xl text-white">$99</span> <span className="text-gray-500 text-sm">one-time · no subscription</span></div>
            <ul className="space-y-2.5 mb-8 flex-1">
              {["Guided, AI-drafted plan", "Bank / SBA / investor format", "3-year financial projections", "AI-matched grants & funding leads"].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-gray-300"><Check size={16} className="text-gold shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
            <button onClick={() => navigate("/business-plan")} data-testid="service-business-plan-cta"
              className="w-full py-3.5 font-semibold flex items-center justify-center gap-2 border border-white/25 text-white hover:bg-white/5 transition-colors">
              Start my free business plan <ArrowRight size={16} />
            </button>
          </div>

          {/* AI Concierge */}
          <div className="border border-gold bg-navy-700 p-8 flex flex-col" data-testid="service-ai-concierge">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 border border-gold/40 flex items-center justify-center text-gold"><Bot size={20} /></div>
              <span className="text-[11px] uppercase tracking-[0.2em] text-gold">Grow it</span>
            </div>
            <h3 className="font-heading text-2xl text-white">AI Concierge</h3>
            <p className="text-gray-400 text-sm mt-2 mb-5">Your own 24/7 AI assistant, trained on your business — answering customers and capturing leads while you sleep.</p>
            <div className="mb-5"><span className="font-heading text-4xl text-white">$597</span> <span className="text-gray-500 text-sm">setup · then $99/mo Concierge Care</span></div>
            <ul className="space-y-2.5 mb-8 flex-1">
              {["Trained on your services & FAQs", "Answers customers 24/7", "Captures & routes leads", "Launch Bundle: $697 incl. 3 months care"].map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-gray-300"><Check size={16} className="text-gold shrink-0 mt-0.5" /> {f}</li>
              ))}
            </ul>
            <button onClick={() => navigate("/ai-concierge")} data-testid="service-ai-concierge-cta"
              className="w-full py-3.5 font-semibold flex items-center justify-center gap-2 bg-gold text-navy hover:bg-gold-light transition-colors">
              Explore AI Concierge <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* $997 STARTER — WHAT'S INCLUDED */}
      <section className="mt-28" data-testid="starter-detail">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-[11px] uppercase tracking-[0.2em] text-gold font-semibold">The Standard Build</span>
        </div>
        <h2 className="font-heading text-3xl md:text-4xl text-white">The $997 Starter Website</h2>
        <p className="text-gray-400 mt-4 max-w-2xl">
          A professional 4-page website delivered in 5–7 business days — everything a
          local service business needs to look credible online and start winning customers.
        </p>

        {/* 4 pages */}
        <div className="grid md:grid-cols-2 gap-5 mt-10">
          {STARTER_PAGES.map((pg, i) => (
            <div key={pg.t} className="border border-white/10 bg-navy-800 p-6">
              <div className="font-mono text-gold text-sm mb-2">{String(i + 1).padStart(2, "0")}</div>
              <h4 className="font-heading text-xl text-white mb-2">{pg.t} Page</h4>
              <p className="text-gray-400 text-sm leading-relaxed">{pg.d}</p>
            </div>
          ))}
        </div>

        {/* included features */}
        <h3 className="font-heading text-2xl text-white mt-14 mb-6">Included with every Starter Website</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
          {STARTER_FEATURES.map((f) => (
            <div key={f} className="flex items-start gap-3 text-sm text-gray-300"><Check size={16} className="text-gold shrink-0 mt-0.5" /> {f}</div>
          ))}
        </div>

        {/* industry variations */}
        <h3 className="font-heading text-2xl text-white mt-14 mb-6">Industry-tuned variations</h3>
        <div className="grid md:grid-cols-3 gap-5">
          {INDUSTRY_VARIATIONS.map((v) => (
            <div key={v.t} className="border border-white/10 bg-navy-800 p-6">
              <h4 className="font-heading text-lg text-white">{v.t}</h4>
              <p className="text-xs text-gray-500 mt-1 mb-4">{v.pages}</p>
              <ul className="space-y-2">
                {v.extras.map((e) => (
                  <li key={e} className="flex items-start gap-2 text-sm text-gray-300"><Star size={13} className="text-gold shrink-0 mt-1" /> {e}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-500 mt-8 max-w-3xl leading-relaxed">
          * To keep delivery fast, the client provides text, logo, photos, and required business
          information before development begins.
        </p>
      </section>

      {/* UPGRADE PRICING */}
      {upgrades.length > 0 && (
        <section className="mt-28" data-testid="upgrade-pricing">
          <h2 className="font-heading text-3xl md:text-4xl text-white">Add-ons & upgrades</h2>
          <p className="text-gray-400 mt-4 max-w-2xl">Grow your site over time — add what you need, when you need it. Select the ones you want and request them; we'll confirm scope and send a quote.</p>
          <div className="mt-8 border border-white/10 divide-y divide-white/10 max-w-2xl">
            {upgrades.map((u) => {
              const on = selected.includes(u.name);
              const slug = u.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return (
                <div key={u.name} className={`w-full flex items-center gap-1 pr-3 transition-colors ${on ? "bg-gold/10" : "hover:bg-white/[0.03]"}`}>
                  <button type="button" onClick={() => toggle(u.name)}
                    data-testid={`upgrade-${slug}`}
                    className="flex-1 flex items-center justify-between gap-4 px-6 py-4 text-left">
                    <span className="flex items-center gap-3">
                      <span className={`w-5 h-5 border flex items-center justify-center shrink-0 transition-colors ${on ? "bg-gold border-gold" : "border-white/30"}`}>
                        {on ? <Check size={13} className="text-navy" /> : <Plus size={12} className="text-gray-500" />}
                      </span>
                      <span className={`text-sm ${on ? "text-white" : "text-gray-200"}`}>{u.name}</span>
                    </span>
                    <span className="text-gold font-semibold text-sm shrink-0">{u.price}</span>
                  </button>
                  {u.desc && (
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button type="button" tabIndex={-1} aria-label={`About ${u.name}`}
                            data-testid={`upgrade-info-${slug}`}
                            className="p-2 text-gray-500 hover:text-gold transition-colors shrink-0">
                            <Info size={15} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-xs text-sm leading-relaxed">{u.desc}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              );
            })}
          </div>

          {selected.length > 0 && (
            <div className="mt-4 max-w-2xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-navy-800 border border-gold/30 px-6 py-4"
              data-testid="addon-selection-bar">
              <div className="text-sm text-gray-300">
                <span className="text-white font-semibold">{selected.length}</span> selected ·{" "}
                <span className="text-gold font-semibold">{hasVariable ? "from " : ""}${estTotal.toLocaleString()}</span>
                <span className="text-gray-500"> estimated</span>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => setSelected([])} className="text-xs text-gray-400 hover:text-white" data-testid="addon-clear">Clear</button>
                <button onClick={() => setShowReq(true)} data-testid="addon-request-open"
                  className="inline-flex items-center gap-2 bg-gold text-navy px-5 py-2.5 text-sm font-semibold hover:bg-gold-light transition-colors">
                  Request {selected.length} add-on{selected.length > 1 ? "s" : ""} <ArrowRight size={15} />
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* CARE PLAN */}
      {care && (
        <section className="mt-28" data-testid="care-plan">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 mb-3"><Shield size={18} className="text-gold" /><span className="text-[11px] uppercase tracking-[0.2em] text-gold font-semibold">Ongoing Support</span></div>
            <h2 className="font-heading text-3xl md:text-4xl text-white">{care.name}</h2>
            <p className="text-gray-400 mt-3 max-w-2xl mx-auto">{care.subtitle || "Keep your website secure, backed up, and up to date after launch."}</p>
          </div>
          {(care.tiers && care.tiers.length) ? (
            <div className="grid md:grid-cols-3 gap-6 items-stretch">
              {care.tiers.map((t) => (
                <div key={t.id} data-testid={`care-tier-${t.id}`}
                  className={`relative flex flex-col border p-7 ${t.highlight ? "border-gold bg-gold/5" : "border-white/10 bg-navy-800"}`}>
                  {t.highlight && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1">{t.highlight_label || "Most popular"}</span>
                  )}
                  <h3 className="font-heading text-xl text-white">{t.name}</h3>
                  <div className="mt-2 mb-1">
                    <span className="font-heading text-4xl text-white">{t.price}</span>
                    <span className="text-gray-500 text-sm"> {t.period}</span>
                  </div>
                  <p className="text-gray-400 text-sm mb-5 min-h-[2.5rem]">{t.best_for}</p>
                  <div className="space-y-2.5 mt-auto">
                    {t.includes.map((c) => (
                      <div key={c} className="flex items-start gap-2.5 text-sm text-gray-300"><Check size={16} className="text-gold shrink-0 mt-0.5" /> {c}</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border border-gold/30 bg-gold/5 p-8">
              <div className="font-heading text-3xl text-white text-center mb-6">{care.price_range}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3">
                {care.includes.map((c) => (
                  <div key={c} className="flex items-start gap-3 text-sm text-gray-300"><Check size={16} className="text-gold shrink-0 mt-0.5" /> {c}</div>
                ))}
              </div>
            </div>
          )}
          <p className="text-center text-gray-500 text-sm mt-6">Not sure which fits? We'll recommend the right plan for you during your project — no pressure.</p>
        </section>
      )}

      {/* PROCESS */}
      <div className="mt-28 grid md:grid-cols-3 gap-8">
        {[
          { n: "01", t: "Discovery", d: "We learn your goals, audience, and brand through the questionnaire and a call." },
          { n: "02", t: "Design & Build", d: "We craft the design, iterate with you, and develop a fast, responsive site." },
          { n: "03", t: "Launch & Support", d: "We test, launch, and support your website — with everything documented." },
        ].map((s) => (
          <div key={s.n} className="border-l border-gold/40 pl-6">
            <div className="font-mono text-gold text-sm mb-2">{s.n}</div>
            <h4 className="font-heading text-xl text-white mb-2">{s.t}</h4>
            <p className="text-gray-400 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-20 text-center border border-white/10 bg-navy-800 p-10">
        <h3 className="font-heading text-2xl md:text-3xl text-white">Ready to get your business online?</h3>
        <p className="text-gray-400 mt-3">Tell us about your project and we'll tailor the right package for you.</p>
        <button onClick={() => navigate("/start")} data-testid="services-cta-start"
          className="mt-7 inline-flex items-center justify-center gap-2 bg-gold text-navy px-8 py-3.5 font-semibold hover:bg-gold-light transition-colors">
          Start My Project <ArrowRight size={16} />
        </button>
      </div>

      {showReq && (
        <AddonRequestModal
          selected={selected}
          upgrades={upgrades}
          estTotal={estTotal}
          hasVariable={hasVariable}
          onClose={() => setShowReq(false)}
          onDone={() => { setShowReq(false); setSelected([]); }}
        />
      )}
    </div>
  );
}

function AddonRequestModal({ selected, upgrades, estTotal, hasVariable, onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", business_name: "", goals: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.error("Please enter a valid email."); return; }
    setBusy(true);
    try {
      await api.post("/inquiries", {
        name: form.name.trim(),
        email: form.email.trim(),
        business_name: form.business_name.trim(),
        goals: form.goals.trim(),
        addons: selected,
        action: "save",
        cta_source: "addons",
      });
      toast.success("Request received! We'll confirm scope and send you a quote shortly.");
      onDone();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="addon-request-modal">
      <div className="bg-navy border border-white/10 w-full max-w-lg max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-gold" />
            <span className="text-white font-heading text-lg">Request your add-ons</span>
          </div>
          <button onClick={onClose} data-testid="addon-modal-close" className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5">
          <div className="bg-navy-800 border border-gold/20 p-4 mb-5">
            <div className="text-[11px] uppercase tracking-wider text-gold mb-2">Selected ({selected.length})</div>
            <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
              {selected.map((n) => {
                const u = upgrades.find((x) => x.name === n) || {};
                return <div key={n} className="flex justify-between text-sm text-gray-300"><span>{n}</span><span className="text-gold">{u.price}</span></div>;
              })}
            </div>
            <div className="flex justify-between text-sm mt-3 pt-3 border-t border-white/10">
              <span className="text-gray-400">Estimated total</span>
              <span className="text-white font-semibold">{hasVariable ? "from " : ""}${estTotal.toLocaleString()}</span>
            </div>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <input value={form.name} onChange={set("name")} placeholder="Your name *" data-testid="addon-name"
              className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
            <input value={form.email} onChange={set("email")} placeholder="Email *" type="email" data-testid="addon-email"
              className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
            <input value={form.business_name} onChange={set("business_name")} placeholder="Business name (optional)" data-testid="addon-business"
              className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none" />
            <textarea value={form.goals} onChange={set("goals")} placeholder="Anything we should know? (optional)" rows={3} data-testid="addon-notes"
              className="w-full bg-navy-800 border border-white/15 text-white px-3 py-2.5 text-sm focus:border-gold focus:outline-none resize-none" />
            <button type="submit" disabled={busy} data-testid="addon-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-gold text-navy px-5 py-3 font-semibold hover:bg-gold-light transition-colors disabled:opacity-50">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />} Send request
            </button>
            <p className="text-[11px] text-gray-500 text-center">No payment now — we'll confirm scope and send a quote. Variable items marked "from" are starting prices.</p>
          </form>
        </div>
      </div>
    </div>
  );
}
